import { Subject, Subscription } from 'rxjs'

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace SharedFuture {
    export interface Options {
        time: {
            ttl: number
        }
    }

    export type Handler<Args, ReturnType> = (args: Args) => Promise<ReturnType>

    export type Job<ReturnType> =
        | {
              subjectList: Subject<ReturnType>[]
              finishedAt: Date
              isRunning: boolean
              resolved: undefined
          }
        | {
              subjectList: Subject<ReturnType>[]
              finishedAt: Date
              isRunning: boolean
              resolved: {
                  value: ReturnType
              }
          }
}

export class SharedFuture<ReturnType, Args> {
    private jobs: {
        [key: string]: SharedFuture.Job<ReturnType> | undefined
    }
    private subscriptionToAll: Subject<ReturnType> = new Subject<ReturnType>()
    private options: SharedFuture.Options
    private isSkippingRun = false

    constructor(
        private handler: SharedFuture.Handler<Args, ReturnType>,
        private keyGenerator: (args: Args) => string,
        options: { time?: { ttl?: number } } = {}
    ) {
        this.jobs = {}

        const defaultTime = {
            ttl: 0,
        }
        const time = {
            ...defaultTime,
            ...(options.time || {}),
        }

        this.options = {
            time,
        }
    }

    subscribeAll(next: (value: ReturnType) => void, error?: (err: Error) => void): Subscription {
        if (this.isSkippingRun) {
            this.isSkippingRun = false
        }
        const subscription = this.subscriptionToAll.subscribe(next, error)
        return subscription
    }
    subscribe(args: Args, next: (value: ReturnType) => void, error?: (err: Error) => void): Subscription {
        // console.log('[ SharedFuture ] - subscribe() - params', { key, args })
        const key = this.generateKey(args)
        const job = this.getJob(key)
        const subject = new Subject<ReturnType>()
        job.subjectList.push(subject)
        const subscription = subject.subscribe(next, error)
        const shouldRunJob = this.shouldRunJob(key)

        if (shouldRunJob) {
            this.runJob(key, args, false)
        } else {
            if (job.resolved) {
                subject.next(job.resolved.value)
            }
        }
        if (this.isSkippingRun) {
            this.isSkippingRun = false
        }
        return subscription
    }

    dontRun() {
        this.isSkippingRun = true
        return this
    }

    toPromise(args: Args): Promise<ReturnType> {
        // console.log('[ SharedFuture ] - subscribe() - args', args)

        let subscription: Subscription | undefined
        return new Promise((resolve, reject) => {
            subscription = this.subscribe(
                args,
                (value) => {
                    if (subscription) {
                        subscription.unsubscribe()
                    }
                    resolve(value)
                },
                (err) => {
                    if (subscription) {
                        subscription.unsubscribe()
                    }
                    reject(err)
                }
            )
        })
    }

    /**
     * This will emit a value for the subscribers
     */
    run(args: Args): this {
        const key = this.generateKey(args)
        this.runJob(key, args, true)
        return this
    }

    reset(args: Args) {
        const key = this.generateKey(args)
        const job = this.getJob(key)
        job.finishedAt = new Date(0)
        delete job.resolved

        return this
    }

    resetAll() {
        const keys = Object.keys(this.jobs)
        keys.forEach((key) => {
            const job = this.getJob(key)
            job.finishedAt = new Date(0)
            delete job.resolved
        })
        return this
    }

    private generateKey(args: Args) {
        const key = this.keyGenerator ? this.keyGenerator(args) : ''
        return key
    }

    private async runJob(key: string, args: Args, forceRunJob: boolean) {
        // console.log('[ SharedFuture] - runJob() - args', args)
        const job = this.getJob(key)

        try {
            job.isRunning = true

            const result = await this.handler(args)

            job.isRunning = false
            job.finishedAt = new Date(Date.now())
            this.subscriptionToAll.next(result)
            job.subjectList.forEach((subject) => subject.next(result))

            job.resolved = {
                value: result,
            }
        } catch (err) {
            job.isRunning = false
            job.subjectList.forEach((subject) => subject.error(err))
            this.subscriptionToAll.error(err)
        }
    }

    private getJob(key: string) {
        let job = this.jobs[key]
        if (job === undefined) {
            job = {
                subjectList: [],
                finishedAt: new Date(0),
                isRunning: false,
                resolved: undefined,
            }
            this.jobs[key] = job
        }
        return job
    }

    private shouldRunJob(key: string): boolean {
        const job = this.getJob(key)
        if (job.isRunning) {
            return false
        }
        if (this.isSkippingRun) {
            // console.log('[ SharedFuture ] - shouldRunJob() - isJob.dontRun', job.dontRun)
            return false
        }
        const { ttl } = this.options.time
        const lastJobFinishedAt = job.finishedAt.getTime()
        if (ttl === 0 && lastJobFinishedAt > 0) {
            return false
        }

        if (Date.now() - lastJobFinishedAt > ttl) {
            return true
        }

        return false
    }
}
